import React, { useMemo, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast, Flip } from 'react-toastify';
import {
  Button,
  Col,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import api from '~/services/api';
import history from '~/services/history';
import { CANCELLED, DELIVERED, SEARCHING } from '~/util/deliveryStatuses';
import { formatWhatsappPhoneNumber } from '~/util/format';
import { getRole } from '~/util/getRole';
import ConfirmPickupModal from '~/components/Modals/ConfirmPickupModal';
import TwoFactorActionModal from '~/components/Modals/TwoFactorActionModal';

const ToastSupportContent = ({ order_number }) => (
  <p>
    Erro ao chamar entregador, fale com o{' '}
    <a
      href={`https://wa.me/${formatWhatsappPhoneNumber(
        '5511935354434'
      )}?text=${encodeURIComponent(
        `Erro ao chamar entregador, você pode me ajudar?%0A%0AID da Venda: %23${order_number}`
      )}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      suporte Viddy
    </a>
    .
  </p>
);

export default function ConsultantManagementButtons({
  sale,
  delivery,
  separated,
  salePaymentReverse,
  toggleFinishCancelledOrder,
  cartIndex,
  cartsArrLength,
  handleFinishUnify,
  handleFinishAndNext,
  setShouldReloadCart,
  setShouldShowColectDeliveryForm,
  setShouldShowPaymentSuitcaseform,
  formRef,
  onOpenCorreiosLabelModal,
  onOpenCorreiosDeclarationModal,
  isDownloadingDeclaration,
  // ─── NOVAS PROPS ───
  onDownloadCorreiosLabel,
  labelStatus,
  onRetryCorreiosLabel,
  // ─── Devolução pelos Correios (logística reversa) ───
  onOpenCorreiosReverseModal,
}) {
  const roles = useSelector(({ user }) => user.profile.roles);

  const [reverseModal, setReverseModal] = React.useState(false);
  const [buttonLoading, setButtonLoading] = React.useState(false);
  const [nextStatusButtonLoading, setNextStatusButtonLoading] =
    React.useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [show2FA, setShow2FA] = useState(false);
  const [actionToken, setActionToken] = useState(null);

  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'salesman',
        'store-manager',
        'financier',
      ]),
    [roles]
  );

  const location = useLocation();
  const toastID = useRef(null);

  const subscriberId = localStorage.getItem('@VIDDY:X-Server');
  const subscriberNomura = '6888af2c-b1c1-431d-a7e6-aea35d2f0286';
  const subscriberNomuraShoes = '83afc2b0-8090-436e-ae8c-8a364ee9839e';
  const subscriberDassFila = '0c47d0cd-537c-41bd-8176-b6e91bf5178f';
  const subscriberNewBalance = '12807b23-1394-4498-8d3b-db9aa48e053e';
  const isReceiptNumberRequired = [
    subscriberNomura,
    subscriberNomuraShoes,
    subscriberDassFila,
    subscriberNewBalance,
  ].includes(subscriberId);

  const handleFinishOrderWhenNotDelivery = async () => {
    try {
      if (isReceiptNumberRequired) {
        const nfceNumber = formRef.current.getFieldValue('receipt_number');

        if (!nfceNumber) {
          toast.error('O número da nota fiscal é obrigatório!', {
            autoClose: 6000,
          });
          formRef.current.setFieldError(
            'receipt_number',
            'Insira um número de nota fiscal'
          );
          setButtonLoading(false);
          return;
        }
      }

      await api.patch(`${role}/carts/${sale.id}/finalize`);

      history.push('/');
      toast.success('Venda finalizada!');
    } catch (err) {
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      }
    }
  };

  const handleMarkAsReady = async () => {
    setButtonLoading(true);
    if (sale.is_for_delivery) {
      if (isReceiptNumberRequired) {
        const nfceNumber = formRef.current.getFieldValue('receipt_number');

        if (!nfceNumber) {
          toast.error('O número da nota fiscal é obrigatório!', {
            autoClose: 6000,
          });
          formRef.current.setFieldError(
            'receipt_number',
            'Insira um número de nota fiscal'
          );
          setButtonLoading(false);
          return;
        }
      }

      try {
        toastID.current = toast.info('Registrando chamada...', {
          autoClose: false,
        });
        await api.patch(`${role}/carts/${sale.id}/mark-as-ready`);

        toast.update(toastID.current, {
          render: 'Chamada registrada com sucesso! Redirecionando...',
          type: toast.TYPE.SUCCESS,
          autoClose: 5000,
          transition: Flip,
        });
        history.push('/');
      } catch (err) {
        setButtonLoading(false);
        toast.update(toastID.current, {
          render: () => (
            <ToastSupportContent order_number={sale.order_number} />
          ),
          type: toast.TYPE.ERROR,
          autoClose: 5000,
          transition: Flip,
          delay: 750,
        });
      }
    }
  };

  const nextStatus = async () => {
    setNextStatusButtonLoading(true);
    try {
      await api.patch(`/utils/update-delivery-for-test/${sale.id}`);

      setShouldReloadCart(true);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao passar para o próximo status.');
    } finally {
      setNextStatusButtonLoading(false);
    }
  };

  const shouldShowNextStatusButton = useMemo(() => {
    if (
      !['teste', 'homologacao', 'treinamento', 'demo', 'localhost'].includes(
        window.location.host?.split('.')?.shift()?.split(':')?.shift()
      )
    )
      return false;

    if (!['BEE', 'MOTTU'].includes(sale.delivery_method)) return false;

    if (sale?.status === 0) return false;

    if (sale.is_suitcase_delivery) {
      if (
        !sale.return_delivery &&
        (sale.status_text === 'Aguardando envio' ||
          sale.status_text === 'Malinha entregue' ||
          sale.delivery?.status === CANCELLED)
      )
        return false;

      if (
        sale.return_delivery &&
        (sale.return_delivery?.status === CANCELLED ||
          sale.status_text === 'Coleta realizada' ||
          sale.status_text === 'Pedido finalizado')
      )
        return false;

      return true;
    }

    if (
      !sale.is_suitcase_delivery &&
      ((sale.delivery &&
        !sale.return_delivery &&
        sale.delivery?.status !== 'CANCELED' &&
        sale?.status > 2 &&
        sale?.status < 4) ||
        (sale.status_text === 'Entregue' &&
          sale.return_delivery &&
          sale.return_delivery?.status !== 'CANCELED'))
    ) {
      return true;
    }

    return false;
  }, [sale]);

  const callTheDelivery = useMemo(
    () => sale?.status === 2 && sale.is_for_delivery,
    [sale]
  );

  const exceptionMoneyAndCart = useMemo(() => {
    if (
      sale.payment_type === 201 ||
      sale.payment_type === 202 ||
      sale.payment_type === 203 ||
      sale.payment_type === 204 ||
      sale.payment_type === 401
    ) {
      return true;
    }
    return false;
  }, [sale]);

  const hideButtonSaleCross = useMemo(
    () =>
      (sale?.status >= 2 && sale.is_cross && role === 'digital-consultant') ||
      (sale.is_cross &&
        sale.status_text === 'Aguardando aceite' &&
        role === 'cross-manager'),
    [sale, role]
  );

  const hideButtonCorreios = useMemo(
    () => sale?.status >= 2 && sale?.delivery_method === 'CORREIOS',
    [sale]
  );

  const hideButtonCorreiosExpress = useMemo(
    () => sale?.status >= 2 && sale?.delivery_method === 'CORREIOS_EXPRESS',
    [sale]
  );

  const showCorreiosLabelButton = useMemo(() => {
    if (
      sale?.is_for_delivery &&
      sale?.delivery_method === 'CORREIOS' &&
      sale?.status_text === 'Aguardando envio' &&
      sale?.delivery?.id_on_service &&
      sale?.delivery?.track_data
    ) {
      return true;
    }
    return false;
  }, [sale]);

  const showCorreiosPrePostagemButton = useMemo(() => {
    const isCorreiosDelivery =
      sale?.is_for_delivery && sale?.delivery_method === 'CORREIOS';

    const hasNoPrePostagem = !sale?.delivery?.id_on_service;

    const isAllowedStatus = sale?.status === 2;

    return Boolean(isCorreiosDelivery && isAllowedStatus && hasNoPrePostagem);
  }, [sale]);

  // ─── Label está pronto para download? ───
  const isLabelProcessing =
    labelStatus === 'pending' || labelStatus === 'processing';
  const isLabelFailed = labelStatus === 'failed';

  const showCorreiosDeclarationButton = useMemo(() => {
    return (
      sale?.is_for_delivery &&
      sale?.delivery_method === 'CORREIOS' &&
      sale?.status_text === 'Aguardando envio' &&
      sale?.delivery?.id_on_service
    );
  }, [sale]);

  // ─── Devolução pelos Correios (logística reversa) ───
  const hasActiveCorreiosReverse = useMemo(
    () =>
      sale?.return_delivery_method === 'CORREIOS' &&
      !!sale?.return_delivery &&
      sale?.return_delivery?.status !== CANCELLED,
    [sale]
  );

  // Qualquer devolução pelos Correios (ativa, cancelada ou expirada)
  const hasCorreiosReverse =
    sale?.return_delivery_method === 'CORREIOS' && !!sale?.return_delivery;

  const showCorreiosReverseButton = useMemo(
    () =>
      Boolean(
        sale?.is_for_delivery &&
          sale?.delivery_method === 'CORREIOS' &&
          sale?.status === 4 &&
          sale?.status_text === 'Entregue' &&
          ![201, 202, 203, 204].includes(sale?.payment_type)
      ),
    [sale]
  );

  const isMarkAsReadyAvaliable = useMemo(() => {
    if (!sale.is_for_delivery && (sale.status === 2 || sale.status === 3))
      return true;

    if (
      sale.is_for_delivery &&
      sale.delivery_method === 'CORREIOS_EXPRESS' &&
      (sale.status === 2 || sale.status === 3)
    )
      return true;

    if (
      ![
        'BEE',
        'MOTTU',
        'UBER',
        'CORREIOS',
        'CORREIOS_EXPRESS',
        'VIDDY_DELIVERIES',
        'LALAMOVE',
        'ENTREGA99',
        'IFOOD_ENVIOS',
        'PROPRIA',
      ].includes(sale.delivery_method)
    )
      return false;

    if (sale?.status === 0) return false;

    if (sale.is_suitcase_delivery) {
      if (
        !sale.return_delivery &&
        (sale.status_text === 'Aguardando envio' ||
          sale.status_text === 'Malinha entregue' ||
          sale.delivery?.status === SEARCHING ||
          sale.delivery?.status === DELIVERED)
      )
        return false;

      if (
        sale.return_delivery &&
        (sale?.status >= 8 ||
          sale.return_delivery?.status === SEARCHING ||
          sale.return_delivery?.status === DELIVERED)
      )
        return false;

      return true;
    }

    if (
      (sale.delivery &&
        sale.delivery?.status !== SEARCHING &&
        sale.delivery?.status !== DELIVERED &&
        !sale?.delivery?.finalization_reason &&
        sale.status_text !== 'Entregue' &&
        sale.status_text !== 'Pedido devolvido' &&
        sale.status_text !== 'Estornado' &&
        sale?.status >= 3) ||
      (sale?.return_delivery &&
        sale?.return_delivery?.status !== SEARCHING &&
        sale?.return_delivery?.status !== DELIVERED &&
        !sale?.return_delivery?.finalization_reason)
    ) {
      return true;
    }

    return false;
  }, [sale]);

  const handleFinish = async () => {
    if (
      sale?.status === 3 &&
      !sale?.is_for_delivery &&
      sale?.confirmation_code
    ) {
      setShowConfirmationModal(true);
      return;
    }

    if (
      [
        'BEE',
        'MOTTU',
        'UBER',
        'CORREIOS',
        'CORREIOS_EXPRESS',
        'VIDDY_DELIVERIES',
        'LALAMOVE',
        'ENTREGA99',
        'IFOOD_ENVIOS',
        'PROPRIA',
      ].includes(sale.delivery_method)
    ) {
      if (delivery?.status !== CANCELLED && sale.is_suitcase_delivery)
        await nextStatus();

      toggleFinishCancelledOrder();
    }

    if (
      !sale.is_for_delivery ||
      sale.delivery_method === 'CORREIOS' ||
      sale.delivery_method === 'CORREIOS_EXPRESS'
    ) {
      handleFinishOrderWhenNotDelivery();
    }
  };

  const shouldShowReturnDeliveryButton = useMemo(
    () =>
      sale?.is_for_delivery &&
      sale?.status === 4 &&
      sale?.status_text === 'Entregue' &&
      !sale?.return_delivery &&
      sale?.delivery_method !== 'IFOOD_ENVIOS' &&
      ![201, 202, 203, 204].includes(sale?.payment_type),
    [sale]
  );

  const shouldShowReversePaymentButton = useMemo(
    () =>
      (sale?.status === 4 && !sale?.is_for_delivery) ||
      (sale?.status === 10 && sale?.status_text === 'Pedido devolvido'),
    [sale]
  );

  const shouldShowGeneratePaymentButton = useMemo(
    () => sale.is_suitcase_delivery && sale?.status_text === 'Coleta realizada',
    [sale]
  );

  const shouldShowColectDeliveryButton = useMemo(
    () =>
      sale.is_suitcase_delivery &&
      sale?.status_text === 'Malinha entregue' &&
      !sale?.return_delivery,
    [sale]
  );

  const shouldShowMarkAsReadyButton = useMemo(
    () => sale.is_suitcase_delivery && sale?.status_text === 'Aguardando envio',
    [sale]
  );

  const shouldShowSuitcaseDeliveryButtons = useMemo(
    () =>
      sale?.is_suitcase_delivery &&
      sale?.status >= 3 &&
      sale?.status < 9 &&
      ['Aguardando envio', 'Malinha entregue', 'Coleta realizada'].includes(
        sale.status_text
      ) &&
      (sale?.status !== 8 ||
        (sale?.status === 8 && (!Number(sale.amount) || !sale.shortened_url))),
    [sale]
  );

  const handle2FASuccess = useCallback(token => {
    setActionToken(token);
    setShow2FA(false);
    setReverseModal(true);
  }, []);

  if (
    ![
      shouldShowSuitcaseDeliveryButtons,
      shouldShowGeneratePaymentButton,
      shouldShowColectDeliveryButton,
      shouldShowMarkAsReadyButton,
      shouldShowNextStatusButton,
      shouldShowReturnDeliveryButton,
      shouldShowReversePaymentButton,
      isMarkAsReadyAvaliable,
      sale.show_reverse_payment_button,
      callTheDelivery,
      showCorreiosLabelButton,
      showCorreiosReverseButton,
    ].includes(true)
  )
    return null;

  return (
    <Row className="w-100 mx-0 mt-0 mb-4 d-flex justify-content-start">
      <Col
        className="px-0 d-flex flex-wrap justify-content-start"
        style={{ gap: '0.5rem' }}
        xs={12}
        xl={6}
      >
        {/* ─── BOTÃO BAIXAR ETIQUETA (atualizado para fluxo S3) ─── */}
        {showCorreiosLabelButton && (
          <Button
            color="primary"
            size="lg"
            onClick={
              isLabelFailed
                ? onRetryCorreiosLabel || (() => {})
                : onDownloadCorreiosLabel || (() => {})
            }
            disabled={isLabelProcessing}
            className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0 d-flex align-items-center justify-content-center"
            style={{ lineHeight: 1 }}
          >
            {(() => {
              if (isLabelProcessing) {
                return (
                  <>
                    <i className="las la-spinner la-spin mr-2" />
                    Gerando Etiqueta...
                  </>
                );
              }
              if (isLabelFailed) {
                return (
                  <>
                    <i className="las la-redo-alt mr-2" />
                    Gerar Etiqueta Novamente
                  </>
                );
              }
              return 'Baixar Etiqueta';
            })()}
          </Button>
        )}

        {showCorreiosDeclarationButton && (
          <Button
            color="primary"
            className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0 d-flex align-items-center justify-content-center"
            size="lg"
            onClick={onOpenCorreiosDeclarationModal}
            disabled={isDownloadingDeclaration}
          >
            {isDownloadingDeclaration ? (
              <>
                <i className="las la-spinner la-spin mr-2" />
                Baixando Declaração...
              </>
            ) : (
              'Baixar Declaração'
            )}
          </Button>
        )}

        {shouldShowNextStatusButton && !hideButtonSaleCross && (
          <Button
            className={`new-layout-btn main mx-0 ${
              sale.is_for_delivery ? 'long-label' : ''
            } font-size-14 button-sale-action`}
            color="primary"
            size="lg"
            onClick={nextStatus}
            disabled={nextStatusButtonLoading}
          >
            Próximo Status
          </Button>
        )}
        {location.pathname.includes('unificar-entregas') ? (
          <>
            {cartIndex + 1 === cartsArrLength ||
            cartIndex === cartsArrLength ? (
              <Button
                color="primary"
                size="lg"
                className="new-layout-btn main long-label mx-0 font-size-14 button-sale-action"
                onClick={() => handleFinishUnify()}
                disabled={!separated}
              >
                Chamar entregador
              </Button>
            ) : (
              <Button
                color="primary"
                size="lg"
                className="new-layout-btn main mx-0 long-label button-sale-action"
                onClick={() => handleFinishAndNext(cartIndex)}
                disabled={!separated}
              >
                Marcar como separado e ir para o próximo
              </Button>
            )}
          </>
        ) : (
          <>
            {sale.show_reverse_payment_button &&
              !hideButtonSaleCross &&
              subscriberId !== subscriberNomura &&
              !exceptionMoneyAndCart && (
                <Button
                  color="danger"
                  size="lg"
                  outline
                  className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0"
                  onClick={() => setShow2FA(true)}
                >
                  Solicitar Estorno
                </Button>
              )}
            {callTheDelivery &&
              !hideButtonSaleCross &&
              !hideButtonCorreios &&
              !hideButtonCorreiosExpress && (
                <Button
                  className={`new-layout-btn main mx-0 ${
                    sale.is_for_delivery ? 'long-label' : ''
                  } font-size-14 button-sale-action`}
                  color="primary"
                  size="lg"
                  onClick={handleMarkAsReady}
                  disabled={buttonLoading}
                >
                  Chamar Entregador
                </Button>
              )}
            {isMarkAsReadyAvaliable &&
              !hideButtonSaleCross &&
              !hasCorreiosReverse && (
                <Button
                  color="primary"
                  className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0"
                  size="lg"
                  onClick={handleFinish}
                >
                  Marcar como entregue
                </Button>
              )}
            {showCorreiosPrePostagemButton && (
              <Button
                color="primary"
                className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0"
                size="lg"
                onClick={onOpenCorreiosLabelModal}
              >
                Pré Postagem
              </Button>
            )}
          </>
        )}
        {shouldShowSuitcaseDeliveryButtons && (
          <>
            {shouldShowMarkAsReadyButton && (
              <Button
                color="primary"
                size="lg"
                className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0"
                onClick={handleMarkAsReady}
              >
                Chamar Entregador
              </Button>
            )}
            {shouldShowColectDeliveryButton && (
              <Button
                color="primary"
                size="lg"
                className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0"
                onClick={() => setShouldShowColectDeliveryForm(true)}
              >
                Solicitar Coleta
              </Button>
            )}
            {shouldShowGeneratePaymentButton && (
              <Button
                color="primary"
                size="lg"
                className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0"
                onClick={() => setShouldShowPaymentSuitcaseform(true)}
              >
                Gerar Cobrança
              </Button>
            )}
          </>
        )}
        {shouldShowReturnDeliveryButton &&
          !hideButtonSaleCross &&
          !hideButtonCorreios &&
          !hideButtonCorreiosExpress && (
            <Button
              color="primary"
              size="lg"
              className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0"
              onClick={() => setShouldShowColectDeliveryForm(true)}
            >
              Devolução
            </Button>
          )}
        {showCorreiosReverseButton && !hideButtonSaleCross && (
          <Button
            color="primary"
            size="lg"
            className="new-layout-btn main mx-0 font-size-14 button-sale-action px-0"
            onClick={onOpenCorreiosReverseModal || (() => {})}
          >
            {hasActiveCorreiosReverse ? 'Etiqueta de Devolução' : 'Devolução'}
          </Button>
        )}
      </Col>
      <Modal isOpen={reverseModal}>
        <ModalHeader toggle={() => setReverseModal(state => !state)}>
          Solicitar Estorno
        </ModalHeader>
        <ModalBody>
          <Row className="w-100 mx-0 mb-3">
            <Label for="groupName">
              Essa ação irá realizar o estorno do valor para o cliente, deseja
              continuar?
            </Label>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            className="new-layout-btn main float-left mb-2 mb-lg-1"
            onClick={() => setReverseModal(false)}
          >
            Cancelar
          </Button>
          <Button
            color="success"
            className="new-layout-btn main"
            onClick={() => {
              salePaymentReverse(true, actionToken);
              setReverseModal(false);
              setActionToken(null);
            }}
          >
            Confirmar
          </Button>{' '}
        </ModalFooter>
      </Modal>
      <ConfirmPickupModal
        isOpen={showConfirmationModal}
        toggle={() => setShowConfirmationModal(false)}
        confirmationCode={sale?.confirmation_code}
        saleId={sale.id}
        role={role}
      />
      <TwoFactorActionModal
        isOpen={show2FA}
        toggle={() => setShow2FA(false)}
        onSuccess={handle2FASuccess}
      />
    </Row>
  );
}
